/*
  Material tab (UI overhaul wave 2, screen 05 ideas 2, 3, 5 and 6). Pure helpers, no React.

  parseQtyInput(raw)            → { value, error } — what a person typed in a quantity cell
  summarizeMaterialKpis(...)    → the five numbers above the table (BOQ lines, Delivered %, Remaining, VO needed, site requests)
  materialRowHistory(...)       → chips of the opened row: VOs, delivery notes, site requests, site needs
  diffMaterialDrafts(...)       → every edited quantity (BOQ qty, Sub BOQ, Sub BOQ qty, delivered per DN) + invalid cells
  deliveryNoteCell(...)         → which line of a delivery note a table row shows (or that the row is outside the note's Sub BOQ)
  buildMaterialSavePayload(...) → body of PUT /projects/{id}/material-quantities (delivery notes as per-cell patches)
  sortByCode(list, key)         → VO-001 … VO-004 (natural order, oldest first)
*/
import { formatDate, formatNumber, toNumber } from '@/lib/format';
import { createMaterialMatcher, materialIdentity } from './projectDetailModel';

const text = (value) => String(value ?? '').trim();
const num = (value) => {
  const parsed = toNumber(value);
  return parsed === null ? 0 : parsed;
};
export const qtyText = (value) => formatNumber(value, { maxDecimals: 2 });

/**
 * How a delivery-note number is shown: a bare number ('851') reads like a quantity next to the quantity
 * columns, so it gets a 'DN ' prefix ('DN 851'). Anything else ('DN123', 'DN-001') is shown as stored.
 * Display only — the stored dn_number never changes.
 */
export const dnDisplay = (value) => {
  const raw = text(value);
  return /^\d+$/.test(raw) ? `DN ${raw}` : raw;
};

/** A typed quantity: '1,200' = 1200, '1.5' = 1.5; empty, text or negative numbers are errors. */
export function parseQtyInput(raw) {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return { value: null, error: 'Enter a number' };
    return raw < 0 ? { value: null, error: 'Must be 0 or more' } : { value: raw, error: '' };
  }
  const input = text(raw);
  if (!input) return { value: null, error: 'Enter a number' };
  if (/^-/.test(input)) return { value: null, error: 'Must be 0 or more' };
  if (!/^[\d.,\s]+$/.test(input)) return { value: null, error: 'Enter a number' };
  const digits = input.replace(/\s/g, '');
  let normalized;
  if (/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(digits)) normalized = digits.replace(/,/g, '');
  else if (digits.includes('.') && digits.includes(',')) return { value: null, error: 'Use a dot for decimals' };
  else normalized = digits.replace(',', '.');
  const value = Number(normalized);
  if (!normalized || !Number.isFinite(value) || (normalized.match(/\./g) || []).length > 1) return { value: null, error: 'Enter a number' };
  return { value, error: '' };
}

const sameQty = (a, b) => Math.abs(num(a) - num(b)) < 1e-9;

export const sortByCode = (list = [], key = 'vo_number') => [...(list || [])]
  .sort((a, b) => String(a?.[key] || '').localeCompare(String(b?.[key] || ''), undefined, { numeric: true, sensitivity: 'base' }));

/** The five KPI numbers (rows = the rows the table shows: material rows, or the Main BOQ rows with Sub BOQs). */
export function summarizeMaterialKpis({ rows = [], lineCount, variationColumns = [], deliveryNoteColumns = [], siteRequests = [] } = {}) {
  const planned = rows.reduce((sum, row) => sum + num(row.totalPlannedQty ?? row.boq_qty), 0);
  const delivered = rows.reduce((sum, row) => sum + num(row.deliveredTotal), 0);
  const voExtra = rows.reduce((sum, row) => sum + num(row.variationTotal), 0);
  const shortRows = rows.filter((row) => num(row.remaining) > 0);
  const remaining = shortRows.reduce((sum, row) => sum + num(row.remaining), 0);
  const voNeeded = rows.filter((row) => num(row.variationNeeded) > 0);
  const openRequests = (siteRequests || []).filter((request) => num(request.remaining_qty ?? request.requested_qty) > 0
    && !['closed', 'delivered', 'completed', 'cancelled'].includes(text(request.status).toLowerCase()));
  const dueDates = openRequests.map((request) => text(request.due_date)).filter(Boolean).sort();
  return {
    lines: lineCount ?? rows.length,
    parts: rows.length,
    // BOQ quantity on the same basis as the other numbers (Sub BOQ projects: Main BOQ rows = per-unit × Sub BOQ qty).
    boq: rows.reduce((sum, row) => sum + num(row.boq_qty), 0),
    voExtra,
    voCount: (variationColumns || []).length,
    planned,
    delivered,
    deliveredPercent: planned > 0 ? Math.round((delivered / planned) * 100) : 0,
    remaining,
    shortLines: shortRows.length,
    completeLines: rows.length - shortRows.length,
    voNeeded: voNeeded.length,
    voNeededCodes: voNeeded.map((row) => text(row.part_number) || text(row.description)).filter(Boolean),
    openRequests: openRequests.length,
    openRequestQty: openRequests.reduce((sum, request) => sum + num(request.remaining_qty ?? request.requested_qty), 0),
    nextDue: dueDates[0] || '',
    deliveryNotes: (deliveryNoteColumns || []).map((column) => dnDisplay(column.dn_number)).filter(Boolean),
  };
}

/**
 * What the opened row shows (05 #6): the VOs that added to the line, the delivery notes, the site requests
 * and — when site needs are recorded — needs vs. what BOQ + VOs give.
 */
export function materialRowHistory(row, { variationColumns = [], deliveryNoteColumns = [] } = {}) {
  const variations = sortByCode(variationColumns)
    .map((variation) => ({ id: variation.id, code: text(variation.vo_number) || 'VO', qty: num(row?.variationById?.[variation.id]) }))
    .filter((entry) => entry.qty !== 0);
  const deliveries = (deliveryNoteColumns || [])
    .map((column) => ({
      id: column.id,
      noteId: column.primary_note_id || (column.note_ids || [])[0] || column.id,
      code: dnDisplay(column.dn_number) || 'DN',
      date: column.delivery_date || '',
      qty: (column.note_ids || [column.id]).reduce((sum, noteId) => sum + num(row?.deliveredByDn?.[noteId]), 0),
    }))
    .filter((entry) => entry.qty !== 0);
  const requests = (row?.siteRequests || []).map((request) => ({
    id: request.id,
    qty: num(request.remaining_qty ?? request.requested_qty),
    requested: num(request.requested_qty),
    due: request.due_date || '',
    status: text(request.status) || 'Open',
    notes: text(request.notes),
    unit: text(request.unit) || text(row?.unit) || 'pcs',
  }));
  const planned = num(row?.totalPlannedQty ?? row?.boq_qty);
  const needs = num(row?.siteNeedsQty) > 0 ? { needs: num(row.siteNeedsQty), planned, extra: Math.max(0, num(row.siteNeedsQty) - planned) } : null;
  return { variations, deliveries, requests, needs, empty: !variations.length && !deliveries.length && !requests.length && !needs };
}

/**
 * 05 #5 preview: rows with every valid, unsaved BOQ quantity applied (changes = diffMaterialDrafts().changes),
 * so Remaining, Progress, VO needed and the totals show the effect of an edit before it is saved. A previewed
 * row gets boqPreview: true (the table marks its derived cells amber, like the edited cell). Unchanged rows
 * are returned as they are (same objects).
 */
export function applyBoqPreview(rows = [], changes = []) {
  const typed = new Map((changes || [])
    .filter((change) => change.kind === 'boq' && change.field === 'boq_qty')
    .map((change) => [change.id, num(change.to)]));
  if (!typed.size) return rows;
  return (rows || []).map((row) => {
    if (!typed.has(row.id)) return row;
    const boqQty = typed.get(row.id);
    const totalPlannedQty = boqQty + num(row.variationTotal);
    return {
      ...row,
      boq_qty: boqQty,
      totalPlannedQty,
      remaining: totalPlannedQty - num(row.deliveredTotal),
      variationNeeded: num(row.siteNeedsQty) - totalPlannedQty,
      boqPreview: true,
    };
  });
}

export const requestLabel = (request) => (
  `Site request · ${qtyText(request.qty)} ${request.unit} · due ${request.due ? formatDate(request.due, { year: 'auto' }) : '—'} · ${request.status}`
);

/**
 * Every quantity edited in the table and not saved yet.
 * - boqItems / boqDrafts      → BOQ qty and Sub BOQ of a line ({ [id]: { boq_qty, sub_boq } })
 * - subBoqQtyDrafts / groups  → Sub BOQ multiplier per group ({ [label]: value }, { [label]: rows })
 * - dnEdits / dnColumns / rows → delivered qty typed in the table ({ [noteId]: { [rowId]: value } }, single-note columns)
 * → { changes: [{ key, kind, id, field, label, from, to }], invalid: [{ key, label, error }] }
 */
export function diffMaterialDrafts({
  boqItems = [], boqDrafts = {}, allowSubBoq = false, subBoqQtyDrafts = {}, groups = {}, dnEdits = {}, dnColumns = [], rows = [],
} = {}) {
  const changes = [];
  const invalid = [];
  const code = (item) => text(item?.part_number) || text(item?.description) || 'BOQ line';
  boqItems.forEach((item) => {
    const draft = boqDrafts[item.id];
    if (!draft) return;
    const key = `boq:${item.id}`;
    if (draft.boq_qty !== undefined && String(draft.boq_qty) !== String(item.boq_qty ?? 0)) {
      const parsed = parseQtyInput(draft.boq_qty);
      if (parsed.error) invalid.push({ key, label: code(item), error: parsed.error });
      else if (!sameQty(parsed.value, item.boq_qty)) {
        changes.push({ key, kind: 'boq', id: item.id, field: 'boq_qty', label: code(item), from: num(item.boq_qty), to: parsed.value });
      }
    }
    if (allowSubBoq && draft.sub_boq !== undefined && text(draft.sub_boq) !== text(item.sub_boq)) {
      changes.push({ key: `sub:${item.id}`, kind: 'boq', id: item.id, field: 'sub_boq', label: code(item), from: text(item.sub_boq), to: text(draft.sub_boq) });
    }
  });
  if (allowSubBoq) {
    Object.entries(subBoqQtyDrafts || {}).forEach(([label, value]) => {
      const groupRows = groups[label] || [];
      if (!groupRows.length || value === undefined || value === '') return;
      const original = groupRows.find((row) => row.sub_boq_qty !== undefined && row.sub_boq_qty !== null)?.sub_boq_qty ?? 1;
      if (String(value) === String(original)) return;
      const key = `subqty:${label}`;
      const parsed = parseQtyInput(value);
      if (parsed.error) invalid.push({ key, label: `${label} qty`, error: parsed.error });
      else if (!sameQty(parsed.value, original)) {
        changes.push({ key, kind: 'subBoqQty', label: `${label} qty`, group: label, ids: groupRows.map((row) => row.id), from: num(original), to: parsed.value });
      }
    });
  }
  (dnColumns || []).forEach((column) => {
    if ((column.note_ids || []).length !== 1) return;
    const noteId = column.primary_note_id;
    const lineQty = dnEdits[noteId] || {};
    rows.forEach((row) => {
      if (!Object.prototype.hasOwnProperty.call(lineQty, row.id)) return;
      const original = num(row.deliveredByDn?.[noteId]);
      const value = lineQty[row.id];
      if (String(value) === String(original)) return;
      const key = `dn:${noteId}:${row.id}`;
      const parsed = parseQtyInput(value);
      if (parsed.error) invalid.push({ key, label: `${column.dn_number} · ${code(row)}`, error: parsed.error });
      else if (!sameQty(parsed.value, original)) {
        changes.push({ key, kind: 'dn', id: noteId, rowId: row.id, label: `${column.dn_number} · ${code(row)}`, from: original, to: parsed.value });
      }
    });
  });
  return { changes, invalid };
}

const subBoqKey = (value) => text(value).toUpperCase();

/**
 * The Sub BOQ a delivery note is for: its own Sub BOQ, else the one Sub BOQ all its lines name; '' = not
 * tied to one. Same rule on the server (projects.py _delivery_note_scope).
 */
export function deliveryNoteScope(note) {
  const explicit = subBoqKey(note?.sub_boq);
  if (explicit) return explicit;
  const labels = [...new Set((note?.line_items || []).map((line) => text(line?.sub_boq)).filter(Boolean))];
  return labels.length === 1 ? subBoqKey(labels[0]) : '';
}

/** false when the note is for one Sub BOQ and the BOQ line belongs to another (the cell stays read-only). */
export function inDeliveryNoteScope(note, item) {
  const scope = deliveryNoteScope(note);
  return !scope || scope === subBoqKey(text(item?.sub_boq) || 'Unassigned');
}

/**
 * What one Material-table cell (BOQ line × delivery note) shows: the note line counted toward that BOQ
 * line (the first one the matcher links to it), or none. A BOQ line outside the note's Sub BOQ never has
 * a line there (inScope false — the cell is read-only).
 * matcher = createMaterialMatcher(boqItems, identity) → { inScope, index (-1 = no line), line }
 */
export function deliveryNoteCell(note, item, matcher) {
  if (!inDeliveryNoteScope(note, item)) return { inScope: false, index: -1, line: null };
  const lines = note?.line_items || [];
  const index = item ? lines.findIndex((line) => line && matcher.deliveryTargetId(line) === item.id) : -1;
  return { inScope: true, index, line: index >= 0 ? lines[index] : null };
}

/**
 * PUT /projects/{id}/material-quantities body.
 * BOQ lines: one entry per line with every changed field. Delivery notes: one patch per typed cell — the
 * note line the cell showed (index + what it held, so a note changed by someone else is refused) or, when
 * the cell had no line, a new line for that BOQ line. The server never rebuilds a note's line list, so
 * lines the table does not show (not linked to any BOQ line, other Sub BOQs) are kept.
 * options: { deliveryNotes, boqItems, identityOf } — the notes and BOQ lines the table was drawn from.
 */
export function buildMaterialSavePayload(changes = [], { deliveryNotes = [], boqItems = [], identityOf = materialIdentity } = {}) {
  const boq = new Map();
  const upsert = (id, patch) => boq.set(id, { id, ...(boq.get(id) || {}), ...patch });
  changes.forEach((change) => {
    if (change.kind === 'boq') upsert(change.id, { [change.field]: change.to });
    if (change.kind === 'subBoqQty') change.ids.forEach((id) => upsert(id, { sub_boq_qty: change.to }));
  });
  const matcher = createMaterialMatcher(boqItems, identityOf);
  const notesById = new Map((deliveryNotes || []).map((note) => [note.id, note]));
  const itemsById = new Map((boqItems || []).map((item) => [item.id, item]));
  const cellsByNote = new Map();
  changes.filter((change) => change.kind === 'dn').forEach((change) => {
    const cell = deliveryNoteCell(notesById.get(change.id), itemsById.get(change.rowId) || { id: change.rowId }, matcher);
    const patch = cell.line
      ? {
        boq_item_id: change.rowId,
        delivered_qty: change.to,
        line_index: cell.index,
        expected_qty: num(cell.line.delivered_qty),
        expected_part_number: text(cell.line.part_number),
        expected_boq_item_id: text(cell.line.boq_item_id),
      }
      : { boq_item_id: change.rowId, delivered_qty: change.to, line_index: null, expected_qty: 0 };
    cellsByNote.set(change.id, [...(cellsByNote.get(change.id) || []), patch]);
  });
  return {
    boq_items: [...boq.values()],
    delivery_notes: [...cellsByNote.entries()].map(([noteId, lines]) => ({ id: noteId, lines })),
  };
}

/* ---------------------------------------------------------------- wave 3 (screen 05 ideas 8 and 9) */

/**
 * 05 #9 "In stock": the store stock of a row's part (GET /projects/{id}/material-stock → items[part]).
 * → { label, detail, tone, available, covers, locations } | null (nothing in stock and nothing needed)
 * tone: 'green' when the stores can cover what is still to deliver, 'muted' when something is still to deliver
 * and the stores have none ("0 in stock" — information, not an alarm), else 'default'.
 */
export function stockCellFor(row, stock) {
  if (!row || !stock) return null;
  const entry = stock.items?.[text(row.part_number)];
  const remaining = Math.max(0, num(row.remaining));
  const available = num(entry?.available);
  const reservedHere = num(entry?.reserved_here);
  if (!entry || (!available && !reservedHere)) {
    return remaining > 0 ? { label: '0 in stock', detail: 'No store has this part', tone: 'muted', available: 0, covers: false, locations: [] } : null;
  }
  const locations = (entry.locations || []).filter((location) => num(location.available) > 0 || num(location.reserved_here) > 0);
  const [best] = locations;
  const others = locations.length - 1;
  const store = String(best.location || '').replace(/\s+Store$/i, '');
  const label = available > 0
    ? `${store} ${qtyText(best.available)}${others > 0 ? ` +${others}` : ''}`
    : `${qtyText(reservedHere)} reserved here`;
  const detail = [
    ...locations.map((location) => `${location.location}: ${qtyText(location.available)} available${num(location.reserved_here) ? ` (${qtyText(location.reserved_here)} reserved for this project)` : ''}`),
  ].join(' · ');
  const covers = remaining > 0 && available >= remaining;
  return { label, detail, tone: covers ? 'green' : remaining > 0 && !available ? 'muted' : 'default', available, covers, locations };
}

/**
 * 05 #8: lines whose site needs exceed BOQ + VOs, for the "Variation needed" callout and the drafted VO.
 * → { count, lines: [{ id, part, description, unit, needed, siteNeeds, planned }], text: 'BE-GT20W.02 (+6), …' }
 */
export function summarizeVariationNeeded(rows = []) {
  const lines = (rows || [])
    .filter((row) => num(row.variationNeeded) > 0)
    .map((row) => ({
      id: row.id,
      part: text(row.part_number) || text(row.description),
      description: text(row.description),
      unit: text(row.unit) || 'pcs',
      needed: num(row.variationNeeded),
      siteNeeds: num(row.siteNeedsQty ?? (num(row.totalPlannedQty) + num(row.variationNeeded))),
      planned: num(row.totalPlannedQty),
    }))
    .sort((a, b) => b.needed - a.needed);
  return {
    count: lines.length,
    lines,
    text: lines.slice(0, 4).map((line) => `${line.part} (+${qtyText(line.needed)})`).join(', ') + (lines.length > 4 ? ` +${lines.length - 4} more` : ''),
  };
}
